import React, { useState, useEffect } from "react";
import { Text, Box, Tag } from "@chakra-ui/react";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { listScheduledJobsByOwnerId } from "services/ScheduleServices";
import Table, { TableColumn, TablePagination } from "../common/Table";

interface AnalystScenariosSelectionProps {
	selectedAnalystId?: string;
	selectedItems?: Set<string>;
	onSelectionChange?: (selectedItems: Set<string>) => void;
}

const AnalystScenariosSelection: React.FC<AnalystScenariosSelectionProps> = ({
	selectedAnalystId,
	selectedItems = new Set(),
	onSelectionChange,
}) => {
	const [jobs, setJobs] = useState<ScheduledJobProto[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchJobs = async (analystId: string) => {
		setIsLoading(true);
		setError(null);
		try {
			const response = await listScheduledJobsByOwnerId(analystId);
			if (response) {
				setJobs(response.filter((job: ScheduledJobProto) => job.type === ScheduledJobType.SCENARIO_JOB));
			}
		} catch (err) {
			setError("Failed to fetch jobs");
			console.error("Error fetching jobs:", err);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (selectedAnalystId) {
			fetchJobs(selectedAnalystId);
		}
	}, [selectedAnalystId]);

	// Table columns
	const columns: TableColumn<ScheduledJobProto>[] = [
		{
			key: "issue",
			label: "Issue",
			sortable: true,
			width: "25%",
			render: (item) => (
				<Text fontSize="sm" fontWeight="600" mb={0} noOfLines={2}>
					{item.header?.name || "No issue description"}
				</Text>
			),
		},
		{
			key: "trigger",
			label: "Trigger",
			sortable: true,
			width: "55%",
			render: (item) => (
				<Text fontSize="sm" mb={0} noOfLines={2} whiteSpace="normal" wordBreak="break-word">
					{item.scheduled_analysis?.trigger || "No trigger specified"}
				</Text>
			),
		},
		{
			key: "status",
			label: "Status",
			sortable: false,
			width: "20%",
			render: (item) => (
				<Tag
					size="sm"
					colorScheme={item.is_active ? "green" : "gray"}
					fontSize="sm"
					fontWeight="600"
				>
					{item.is_active ? "Active" : "Inactive"}
				</Tag>
			),
		},
	];

	// Handle selection change
	const handleSelectionChange = (newSelectedItems: Set<string>) => {
		if (onSelectionChange) {
			onSelectionChange(newSelectedItems);
		}
	};

	const pagination: TablePagination = {
		total: jobs.length,
		offset: 0,
		has_more: false,
	};

	// Show message if no analyst is selected
	if (!selectedAnalystId) {
		return (
			<Box height="100%" overflowY="hidden" textAlign="center">
				<Text color="gray.500" mb={0}>
					Please select an analyst first
				</Text>
			</Box>
		);
	}

	return (
		<Box height="100%" overflowY="hidden">
			<Table
				data={jobs}
				columns={columns}
				loading={isLoading}
				error={error}
				pagination={pagination}
				selectable={true}
				selectedItems={selectedItems}
				onSelectionChange={handleSelectionChange}
				getItemId={(item) => item.header?.id || ""}
				emptyMessage="No jobs found"
				emptySubMessage="This analyst does not have any scheduled jobs configured"
				hideSelectionCount={true}
			/>
		</Box>
	);
};

export default AnalystScenariosSelection;
